import type {
  ACCESSAlignmentResultCode,
  ACCESSEligibilityResultCode,
} from '@/types/registration';
import type { WregStep } from './wregSteps';

export type WregScenarioId =
  | 'happy'
  | 'bh'
  | 'pending'
  | 'noqualify'
  | 'already'
  | 'control'
  | 'medicare'
  | 'services'
  | 'clinical'
  | 'mismatch'
  | 'switch'
  | 'align-mismatch'
  | 'align-error'
  | 'labs-none'
  | 'labs-missing';

export type WregScenario = {
  id: WregScenarioId;
  label: string;
  group: 'Happy Flow' | 'Eligibility' | 'Alignment' | 'Labs';
};

export const WREG_SCENARIOS: WregScenario[] = [
  { id: 'happy', label: 'Happy path — eligible, aligns, labs on file', group: 'Happy Flow' },
  { id: 'bh', label: 'Eligible for behavioral health only', group: 'Happy Flow' },
  { id: 'pending', label: 'Pending diagnosis — confirm condition', group: 'Happy Flow' },
  { id: 'noqualify', label: 'No qualifying condition', group: 'Eligibility' },
  { id: 'already', label: 'Already aligned elsewhere (90-day)', group: 'Eligibility' },
  { id: 'control', label: 'Control group — CMS message', group: 'Eligibility' },
  { id: 'medicare', label: 'Not Medicare', group: 'Eligibility' },
  { id: 'services', label: 'Hospice, ESRD dialysis, or PACE', group: 'Eligibility' },
  { id: 'clinical', label: 'Clinical exclusion, all tracks', group: 'Eligibility' },
  { id: 'mismatch', label: 'Details mismatch — re-enter step 1', group: 'Eligibility' },
  {
    id: 'switch',
    label: 'Eligible for one track, enrolled with other provider, switch required',
    group: 'Alignment',
  },
  {
    id: 'align-mismatch',
    label: 'Details mismatch at alignment, after the address',
    group: 'Alignment',
  },
  { id: 'align-error', label: 'Processing error, then retry', group: 'Alignment' },
  { id: 'labs-none', label: 'Beneficiary has no labs', group: 'Labs' },
  { id: 'labs-missing', label: 'Says yes, nothing returned', group: 'Labs' },
];

export const WREG_SCENARIO_END: Partial<Record<WregScenarioId, WregStep>> = {
  noqualify: 'x-noqualify',
  already: 'x-already',
  control: 'x-control',
  medicare: 'x-notelig',
  services: 'x-notelig',
  clinical: 'x-notelig',
  mismatch: 'x-mismatch',
  switch: 'x-declined',
  'align-mismatch': 'x-mismatch',
  'align-error': 'x-error',
  'labs-none': 'x-outreach',
  'labs-missing': 'x-labspending',
};

export const WREG_SCENARIO_CODE: Record<WregScenarioId, ACCESSEligibilityResultCode> = {
  happy: 'eligible',
  bh: 'eligible',
  pending: 'eligible-pending-diagnosis',
  switch: 'eligible-switch-participants',
  noqualify: 'not-eligible-diagnoses',
  already: 'not-eligible-already-aligned',
  control: 'not-eligible-control-group',
  medicare: 'not-eligible-not-medicare',
  services: 'not-eligible-services',
  clinical: 'not-eligible-clinical-exclusion',
  mismatch: 'not-eligible-mismatch',
  'align-mismatch': 'eligible',
  'align-error': 'eligible',
  'labs-none': 'eligible',
  'labs-missing': 'eligible',
};

export const WREG_SCENARIO_ALIGN_CODE: Partial<
  Record<WregScenarioId, ACCESSAlignmentResultCode>
> = {
  switch: 'aligned-switch-approved',
  'align-mismatch': 'not-aligned-mismatch',
};

export type WregTrackId = 'eckm' | 'ckm' | 'bh';

export type WregOfferedTrack = {
  id: WregTrackId;
  pending?: boolean;
  ctl?: boolean;
};

export type WregEligibility = {
  tracks: WregOfferedTrack[];
  gate?: boolean;
  blocked?: WregTrackId;
};

const WREG_ELIG: Partial<Record<WregScenarioId, WregEligibility>> = {
  happy: { tracks: [{ id: 'ckm' }, { id: 'eckm' }] },
  bh: { tracks: [{ id: 'bh' }] },
  pending: {
    tracks: [{ id: 'ckm', pending: true }, { id: 'eckm', pending: true }, { id: 'bh', pending: true }],
    gate: true,
  },
  noqualify: { tracks: [{ id: 'ckm', pending: true }] },
  control: { tracks: [{ id: 'ckm' }, { id: 'bh', ctl: true }] },
  switch: { tracks: [{ id: 'ckm' }] },
  'align-error': { tracks: [{ id: 'ckm' }] },
  'labs-none': { tracks: [{ id: 'eckm' }] },
  'labs-missing': { tracks: [{ id: 'eckm' }] },
};

export const wregElig = (id: WregScenarioId): WregEligibility =>
  WREG_ELIG[id] ?? { tracks: [{ id: 'ckm' }, { id: 'eckm' }] };

export const WREG_DISCLOSURE_VARIANTS = ['A', 'B'] as const;
export type WregDisclosure = (typeof WREG_DISCLOSURE_VARIANTS)[number];
